import React, { useEffect, useMemo, useState } from 'react';
import { Search, UserCheck } from 'lucide-react';
import { fetchBridgeClients } from '../../services/BridgeClientsRepository';
import { hydrateAndBuildCanonicalClientQualification } from '../../config/ClientSchema';

const mdBoldToHtml = (text) => {
  const raw = String(text || '');
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\n/g, '<br/>');
};

const copyHtmlToClipboard = async (html) => {
  const blob = new Blob([html], { type: 'text/html' });

  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
    return;
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(String(html).replace(/<[^>]+>/g, ' '));
    return;
  }

  throw new Error('Clipboard indisponível neste navegador/contexto.');
};

const extractClientLabel = (client) => {
  const q = client?.qualification;
  const type = client?.type;

  const pfName = q?.physical?.name;
  const pjName = q?.legal?.companyName;
  const minorName = q?.minor?.name;
  const animalName = q?.animal?.name;
  const imprisonedName = q?.imprisoned?.name;
  const estateName = q?.estate?.deceasedName;
  const condoName = q?.condominium?.name;

  const name = pfName || pjName || minorName || animalName || imprisonedName || estateName || condoName || client?.title || 'Cliente';

  const cpf = q?.physical?.cpf || q?.minor?.cpf || q?.imprisoned?.cpf || q?.estate?.cpf;
  const cnpj = q?.legal?.cnpj || q?.condominium?.cnpj;

  const docId = cpf || cnpj || '';
  return { name, docId, type };
};

export default function BridgeClients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qText, setQText] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      setLoading(true);
      try {
        const docs = await fetchBridgeClients();
        if (!alive) return;
        setItems(docs);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    run();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const term = String(qText || '').toLowerCase().trim();
    if (!term) return items.slice(0, 20);

    return items
      .filter((it) => {
        const { name, docId } = extractClientLabel(it);
        const deep = `${name} ${docId} ${(it?.searchKeywords || []).join(' ')}`.toLowerCase();
        return deep.includes(term);
      })
      .slice(0, 20);
  }, [items, qText]);

  const handleCopy = async (client) => {
    try {
      const canonical = await hydrateAndBuildCanonicalClientQualification(client);
      const md = String(canonical?.text || '[Qualificação não gerada]').trim();
      const htmlBody = mdBoldToHtml(md);
      const html = `<div style="font-family: 'Roboto', sans-serif; font-size: 11pt;">${htmlBody}</div>`;
      await copyHtmlToClipboard(html);
      setCopiedId(client.id);
      window.setTimeout(() => setCopiedId((cur) => (cur === client.id ? null : cur)), 1500);
    } catch (e) {
      alert(e?.message || 'Falha ao copiar qualificação.');
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={qText}
          onChange={(e) => setQText(e.target.value)}
          placeholder="Buscar cliente..."
          className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:bg-white focus:border-[#C5A059]"
        />
      </div>

      {loading ? (
        <div className="text-xs text-gray-500 px-1 py-4">Carregando clientes...</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((client) => {
            const { name, docId } = extractClientLabel(client);
            return (
              <div key={client.id} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="text-xs font-bold text-gray-900 truncate">{String(name).toUpperCase()}</div>
                <div className="mt-1 text-[10px] text-gray-500 truncate">{docId ? `CPF/CNPJ: ${docId}` : 'CPF/CNPJ: n/d'}</div>
                <button
                  type="button"
                  onClick={() => handleCopy(client)}
                  className="mt-2 w-full h-8 rounded-lg bg-[#20243B] text-white text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-2"
                  title="Copiar qualificação completa"
                >
                  <UserCheck className="w-4 h-4" />
                  {copiedId === client.id ? 'Copiado' : 'Copiar Qualificação'}
                </button>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-xs text-gray-500 px-1 py-6 text-center">
              Nenhum cliente encontrado.
            </div>
          )}
        </div>
      )}
    </div>
  );
}